<template>
	<!-- Related Article bottom module on Desktop and Tablet -->
	<aside
		v-if="nextArtData && nextArtData.url"
		:class="['module', 'module-next-article', 'single-link']"
		v-metric="{ module: 'art-bot-dd' }"
	>
		<a
			:href="nextArtData.url"
			v-metric="{ link: '' }"
		>
			<span
				v-if="isOncology"
				class="next-text"
			>
				Next Article
			</span>
			<h3 v-else>
				Related
			</h3>
			<span class="link-text">
				{{ nextArtData.title }}
			</span>
			<i class="webmd-icon-right-arrow"></i>
		</a>
	</aside>
	<aside
		v-else-if="moduleData &&
			moduleData.items &&
			moduleData.items.next &&
			moduleData.items.next.length > 0"
		:class="[
			'module',
			'module-next-article',
			showAllArticles ? 'show-more-articles' : ''
		]"
		:data-chronicle="chronicId"
		:data-e2e="metrics.module"
		data-module-type="NextArticleNavigation"
		v-metric="{ module: metrics.module }"
	>
		<span class="next-text">
			{{ getModuleTitle }}
		</span>

		<ul class="next-article-list">
			<li
				v-for="(item, index) in moduleData.items.next"
				:key="index"
			>
				<a
					:href="item.link"
					:class="{ 'hidden': !showAllArticles && index > 0 }"
					v-metric="{ link: (index + 1) }"
				>
					{{ item.text }}
					<i class="webmd-icon-right-arrow"></i>
				</a>
			</li>
		</ul>

		<a
			v-if="moduleData.items.next.length > 1"
			@click="toggleShowAllArticles"
			class="toggle-link"
		>
			{{ showAllArticles ? 'Show less articles' : 'Show more articles' }}
			<i class="webmd-icon-down-arrow"></i>
		</a>
	</aside>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import useStore from 'webmd-cms-store/src/store';

import has from 'lodash/has';
import isArray from 'lodash/isArray';
import infiniteArticleMixin from '../mixins/infiniteArticle.mixins.es6';

export default {
	name: 'NextArticleNavigation',
	props: {
		isDev: {
			type: Boolean,
			default: false,
		},
		nextArticleData: {
			type: Object,
			default: () => ({}),
		},
	},
	mixins: [infiniteArticleMixin],

	data() {
		return {
			metrics: {
				module: 'art-bot-dd',
			},
			moduleByChronicId: {},
			moduleData: {},
			nextArtInFlow: {},
			nextArtData: {},
			showAllArticles: false,
		};
	},
	created() {
		this.store = useStore(this.$pinia);
		this.moduleByChronicId = this.getModuleByChronicId;
		this.moduleData = this.getItems;
		this.nextArtInFlow = this.nextArticleData;
	},
	computed: {
		getModuleByChronicId() {
			return this.store.cms.getModules('shared_object');
		},

		getModuleTitle() {
			let title = this.moduleData?.moduleTitle?.text || this.store?.pagedata?.omniture?.s_channel_health || '';
			if (title) {
				title = `Next In ${title}`;
			} else {
				title = 'Next';
			}
			return title;
		},

		getItems() {
			const module = has(this.moduleByChronicId, 'article_publication')
				? this.moduleByChronicId
				: {};

			// short circuit if module malformed
			if (Object.keys(module).length === 0) {
				return [];
			}

			let moduleTitleText;

			// getting data for guide
			const Guideitems = module && has(module, 'Guide_Article_Data.content_section.cons_guide_outline.chapter_groups.chapter_group')
				&& isArray(module.Guide_Article_Data.content_section
					.cons_guide_outline.chapter_groups.chapter_group)
				? module.Guide_Article_Data.content_section.cons_guide_outline.chapter_groups.chapter_group
				: [];

			// getting data for bundle
			const Bundleitems = module && has(module, 'Bundle_Article_Data.content_section.cons_topic_outline.section_groups.section_group')
				&& isArray(module.Bundle_Article_Data
					.content_section.cons_topic_outline.section_groups.section_group)
				? module.Bundle_Article_Data.content_section
					.cons_topic_outline.section_groups.section_group.map((link) => ({
						text: link.section_title || '',
						// eslint-disable-next-line no-underscore-dangle
						link: link.section_link.link_friendlyUrl || '',
						/* eslint no-underscore-dangle: 0 */
						chronicId: link.section_link._chronic_id || '',
					}))
				: [];

			// recognizing data (guide or bundle)
			let items = module && has(module, 'article_publication') && module.article_publication === 'WebMD Guide' ? Guideitems : Bundleitems;

			// getting articleid from route here
			const { params: { articleid } } = this.store.route;

			// recognizing next article data (guide or bundle)
			if (items && has(module, 'article_publication') && module.article_publication === 'WebMD Guide') {
				// Getting next article data for guide
				let articleGroupArray = [];
				if (Guideitems && Guideitems.length > 0) {
					for (let i = 0; i < Guideitems.length; i += 1) {
						// eslint-disable-next-line max-len
						const sectionGroups = Guideitems[i]?.chapter_link?.cons_guide_chapter_outline?.section_groups;

						// eslint-disable-next-line max-len
						if (sectionGroups && has(sectionGroups, 'section_group') && isArray(sectionGroups.section_group)) {
							const groupArray = sectionGroups.section_group;
							for (let k = 0; k < groupArray.length; k += 1) {
								articleGroupArray = articleGroupArray
									.concat(groupArray[k].article_groups.article_group);
							}
						} else {
							articleGroupArray = articleGroupArray
								.concat(sectionGroups.section_group.article_groups.article_group);
						}
						/* eslint no-underscore-dangle: 0 */
						const activeIndex = articleGroupArray.findIndex(
							(each) => each.article_link._chronic_id === articleid,
						);

						const activearray = articleGroupArray.find(
							(each) => each.article_link._chronic_id === articleid,
						);

						const actArrChapid = activearray?.article_link?.chapter__chronic_id;

						if (activeIndex !== -1) {
							let nextItems = [];

							if ((activeIndex + 1) < articleGroupArray.length) {
								let count = 0;
								for (let m = activeIndex + 1; m < articleGroupArray.length; m += 1) {
									const item = articleGroupArray[m];
									const chapterId = item?.article_link?.chapter__chronic_id;
									const itemArtId = item?.article_link?._chronic_id;
									if ((chapterId === actArrChapid) && (itemArtId !== articleid)) {
										nextItems.push(item);
										count += 1;
									}
									if (count === 5) {
										break;
									}
								}
								if (count < 5) {
									for (let n = 0; n < activeIndex + 1; n += 1) {
										const item = articleGroupArray[n];
										const chapterId = item?.article_link?.chapter__chronic_id;
										const itemArtId = item?.article_link?._chronic_id;
										if ((chapterId === actArrChapid) && (itemArtId !== articleid)) {
											nextItems.push(item);
											count += 1;
										}
										if (count === 5) {
											break;
										}
									}
								}
							} else {
								nextItems = articleGroupArray.slice(
									activeIndex + 1,
									Math.min(activeIndex + 6, articleGroupArray.length),
								);
							}
							items = {
								next: nextItems
									.filter((item) => item?.article_link?.chapter__chronic_id === actArrChapid)
									.map((item) => ({
										text: item.article_link_title,
										link: item.article_link.link_friendlyUrl,
									})),
							};
						}
						if (items && items.next && items.next.length === 5) { break; }
					}

					const currentItem = Guideitems.find((guideData) => {
						// eslint-disable-next-line max-len
						const sectionGroups = guideData?.chapter_link?.cons_guide_chapter_outline?.section_groups;
						if (!sectionGroups) return '';

						if (isArray(sectionGroups?.section_group)) {
							return sectionGroups?.section_group?.some((group) => {
								if (!group || !group.article_groups) return false;
								// eslint-disable-next-line max-len
								if (isArray(group?.article_groups?.article_group)) {
									return group?.article_groups?.article_group?.some((articleGroup) => articleGroup?.article_link && articleGroup.article_link?._chronic_id === articleid);
								} else {
									return group?.article_groups?.article_group?.article_link?._chronic_id === articleid;
								}
							});
						} else {
							return sectionGroups?.section_group?.article_groups?.article_group.some((articleGroup) => articleGroup.article_link?._chronic_id === articleid);
						}
					}) || {};

					// Getting moduleTitleText for Guide
					moduleTitleText = currentItem?.chapter_link?.cons_guide_chapter_outline?.chapter_title ?? '';
				}
			} else {
				// Getting moduleTitleText for Bundle
				moduleTitleText = module && has(module, 'Bundle_Article_Data.content_section.cons_topic_outline.section_groups')
					&& isArray(module.Bundle_Article_Data.content_section.cons_topic_outline
						.section_groups.section_group)
					&& has(module, 'Bundle_Article_Data.metadata_section.title')
					? module.Bundle_Article_Data.metadata_section.title
					: '';

				// Getting next article data for bundle
				// eslint-disable-next-line max-len
				const activeIndex = Bundleitems && Bundleitems.findIndex((each) => each.chronicId === articleid);

				if (activeIndex !== -1) {
					const others = Bundleitems.filter((b, i) => i !== activeIndex);
					const prevItems = others.slice(0, activeIndex);
					const nextItems = others.slice(activeIndex);
					const combinedItems = [...nextItems, ...prevItems];
					items.next = combinedItems.slice(0, 5);
				} else {
					items = {};
				}
			}

			return {
				moduleTitle: {
					text: moduleTitleText,
				},
				items,
			};
		},
		isOncology() {
			return this.store.pagedata.isOncology;
		},
	},

	methods: {
		toggleShowAllArticles() {
			this.showAllArticles = !this.showAllArticles;
			if (this.showAllArticles) {
				this.$vwmdPageLink('expandmore');
			} else {
				this.$vwmdPageLink('expandless');
			}
		},
	},

	mounted() {
		const self = this;

		if (this.$route && this.$route.query && this.$route.query.qap) {
			this.qap = true;
		}

		if (self.nextArtInFlow && !self.qap) {
			self.nextArtData = {
				title: self.nextArtInFlow.title,
				url: self.nextArtInFlow.friendlyUrl,
			};
		} else if (!self.qap) {
			setTimeout(() => {
				self.getNextAssetRec().then((res) => {
					self.nextArtData = {
						title: res.title,
						url: res.url,
					};
				});
			}, 3500);
		}
	},

};
</script>

<style lang="scss" src="../stylesheets/nextArticleModule/nextart-module.scss"></style>
